import { c as _c } from "react/compiler-runtime";
/**
 * Hook for building team form data from matches.
 * Shared logic used by TeamForm component across different contexts.
 */

import { useMemo } from "react";
import type { FUSportsMatch, FUSportsTeamCompetitor } from "fansunited-data-layer";
import type { FUSportsMatchWithNativeId } from "@/utilities/data/types";
export interface FormResult {
  matchId: string;
  date: string | Date;
  result: "W" | "D" | "L";
  score: string;
  isHome: boolean;
  opponent: {
    id: string;
    name: string;
    logo?: string;
    countryCode?: string;
    countryFlag?: string;
    primaryColor?: string;
    secondaryColor?: string;
  };
}
export interface FormData {
  competitorId: string;
  results: FormResult[];
}
export interface UseTeamFormDataOptions {
  teamId: string;
  matches: FUSportsMatch[];
  variant?: "all" | "home" | "away";
  /** Maximum number of matches to return */
  limit?: number;
}

/**
 * Build form data from a list of matches for a specific team.
 * Filters by home/away variant and calculates W/D/L results.
 */
export function useTeamFormData(t0) {
  const $ = _c(13);
  const {
    teamId,
    matches,
    variant: t1,
    limit: t2
  } = t0;
  const variant = t1 === undefined ? "all" : t1;
  const limit = t2 === undefined ? 8 : t2;
  let t3;
  if ($[0] !== limit || $[1] !== matches || $[2] !== teamId || $[3] !== variant) {
    bb0: {
      if (!matches || matches.length === 0) {
        t3 = null;
        break bb0;
      }
      let t4;
      if ($[5] !== teamId || $[6] !== variant) {
        t4 = match => {
          const isHome = match.competitorOne.id === teamId;
          const isAway = match.competitorTwo.id === teamId;
          if (!isHome && !isAway) {
            return false;
          }
          const statusType = match.status?.type?.toUpperCase();
          if (statusType !== "FINISHED") {
            return false;
          }
          if (variant === "home") {
            return isHome;
          }
          if (variant === "away") {
            return isAway;
          }
          return true;
        };
        $[5] = teamId;
        $[6] = variant;
        $[7] = t4;
      } else {
        t4 = $[7];
      }
      const teamMatches = matches.filter(t4).sort(_temp).slice(0, limit);
      if (teamMatches.length === 0) {
        t3 = null;
        break bb0;
      }
      let t5;
      if ($[8] !== teamId) {
        t5 = match_0 => {
          const isHome_0 = match_0.competitorOne.id === teamId;
          const teamScore = isHome_0 ? parseInt(match_0.score?.competitorOne || "0") : parseInt(match_0.score?.competitorTwo || "0");
          const opponentScore = isHome_0 ? parseInt(match_0.score?.competitorTwo || "0") : parseInt(match_0.score?.competitorOne || "0");
          let result;
          if (teamScore > opponentScore) {
            result = "W";
          } else {
            if (teamScore < opponentScore) {
              result = "L";
            } else {
              result = "D";
            }
          }
          const opponent = isHome_0 ? match_0.competitorTwo : match_0.competitorOne;
          const opponentTeam = opponent as FUSportsTeamCompetitor;
          return {
            matchId: match_0.id,
            date: match_0.timing.kickoffTime,
            result,
            score: `${match_0.score?.competitorOne || "0"}-${match_0.score?.competitorTwo || "0"}`,
            isHome: isHome_0,
            opponent: {
              id: opponent.id,
              name: opponent.name,
              logo: opponent.assets?.logo,
              countryCode: opponentTeam.country?.code,
              countryFlag: opponentTeam.country?.flag,
              primaryColor: opponentTeam.metadata?.branding?.primaryColor,
              secondaryColor: opponentTeam.metadata?.branding?.secondaryColor
            }
          };
        };
        $[8] = teamId;
        $[9] = t5;
      } else {
        t5 = $[9];
      }
      const results = teamMatches.map(t5);
      let t6;
      if ($[10] !== results || $[11] !== teamId) {
        t6 = {
          competitorId: teamId,
          results
        };
        $[10] = results;
        $[11] = teamId;
        $[12] = t6;
      } else {
        t6 = $[12];
      }
      t3 = t6;
    }
    $[0] = limit;
    $[1] = matches;
    $[2] = teamId;
    $[3] = variant;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/**
 * Create a lookup map from Sportal365 match ID to native FansUnited match ID.
 * Used for navigation to match pages.
 */
function _temp(a, b) {
  return new Date(b.timing.kickoffTime).getTime() - new Date(a.timing.kickoffTime).getTime();
}
export function useMatchIdLookup(matches) {
  const $ = _c(2);
  let map;
  if ($[0] !== matches) {
    map = new Map();
    for (const match of matches) {
      const matchWithNativeId = match as FUSportsMatchWithNativeId;
      if (matchWithNativeId.fansUnitedMatchId) {
        map.set(match.id, matchWithNativeId.fansUnitedMatchId);
      }
    }
    $[0] = matches;
    $[1] = map;
  } else {
    map = $[1];
  }
  return map;
}